import type { CourseOverviewDetails } from "./CourseOverviewView";

export type LessonProgressSnapshot = {
  isCompleted: boolean;
  lastAccessedAt: string | null;
};

export type LessonProgressByKey = Record<string, LessonProgressSnapshot>;

export function lessonProgressKey(moduleId: number, lessonId: number): string {
  return `${moduleId}-${lessonId}`;
}

export function resolveBeginCourseLesson(
  courseDetails: CourseOverviewDetails,
): { moduleId: number; lessonId: number } | null {
  const firstModule = courseDetails.modules[0];
  const firstLesson = firstModule?.lessons[0];
  if (!firstModule || !firstLesson) return null;

  return { moduleId: firstModule.id, lessonId: firstLesson.id };
}

export function resolveContinueCourseLesson(
  courseDetails: CourseOverviewDetails,
  lessonProgressByKey: LessonProgressByKey,
): { moduleId: number; lessonId: number } | null {
  const firstTarget = resolveBeginCourseLesson(courseDetails);
  if (!firstTarget) return null;

  let mostRecent: { moduleId: number; lessonId: number; accessedAt: number } | null = null;

  for (const module of courseDetails.modules) {
    for (const lesson of module.lessons) {
      const entry = lessonProgressByKey[lessonProgressKey(module.id, lesson.id)];
      if (!entry?.lastAccessedAt) continue;

      const accessedAt = Date.parse(entry.lastAccessedAt);
      if (Number.isNaN(accessedAt)) continue;

      if (!mostRecent || accessedAt > mostRecent.accessedAt) {
        mostRecent = { moduleId: module.id, lessonId: lesson.id, accessedAt };
      }
    }
  }

  if (mostRecent) {
    return { moduleId: mostRecent.moduleId, lessonId: mostRecent.lessonId };
  }

  for (const module of courseDetails.modules) {
    for (const lesson of module.lessons) {
      const entry = lessonProgressByKey[lessonProgressKey(module.id, lesson.id)];
      if (!entry?.isCompleted) {
        return { moduleId: module.id, lessonId: lesson.id };
      }
    }
  }

  return firstTarget;
}

export function hasAnyCourseActivity(
  progressPercentage: number,
  lessonProgressByKey: LessonProgressByKey,
): boolean {
  if (progressPercentage > 0) return true;

  return Object.values(lessonProgressByKey).some(
    (entry) => entry.isCompleted || Boolean(entry.lastAccessedAt),
  );
}

/** Count lessons across modules (order-independent progress denominator). */
export function countCourseLessons(courseDetails: CourseOverviewDetails | null | undefined): number {
  if (!courseDetails) return 0;
  return courseDetails.modules.reduce((sum, module) => sum + module.lessons.length, 0);
}

/**
 * Progress from completed / total lessons.
 * Any lesson can be marked complete in any order; the bar is just the ratio.
 */
export function computeCourseCompletionPercentage(
  completedLessons: number,
  totalLessons: number,
): number {
  if (totalLessons <= 0) return 0;
  const pct = (completedLessons / totalLessons) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

export function countCompletedLessons(lessonProgressByKey: LessonProgressByKey): number {
  return Object.values(lessonProgressByKey).filter((entry) => entry.isCompleted).length;
}
